import React, { useEffect, useState } from 'react';
import api from '../../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { useToast } from '../../../hooks/use-toast';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell,
} from 'recharts';

const COLORS = ['#0f766e', '#f59e0b', '#3b82f6', '#8b5cf6', '#ef4444', '#14b8a6'];

function Kpi({ label, value }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value ?? '—'}</div>
      </CardContent>
    </Card>
  );
}

function money(n) {
  return `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

export default function CrmPayrollDashboard() {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get('/crm/payroll/dashboard')
      .then((r) => setData(r.data?.data || null))
      .catch((e) => toast({ title: 'Error', description: e.response?.data?.message, variant: 'destructive' }))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="text-muted-foreground">Loading dashboard…</div>;
  if (!data) return <div className="text-muted-foreground">No dashboard data</div>;

  const k = data.kpis || {};
  const charts = data.charts || {};

  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        <Kpi label="Total Employees" value={k.total_employees} />
        <Kpi label="Active Employees" value={k.active_employees} />
        <Kpi label="Current Month Payroll" value={money(k.current_month_payroll)} />
        <Kpi label="Total Salary Paid" value={money(k.total_salary_paid)} />
        <Kpi label="Pending Salary" value={money(k.pending_salary)} />
        <Kpi label="Payroll in Process" value={k.payroll_in_process} />
        <Kpi label="Upcoming Salary Date" value={k.upcoming_salary_date || '—'} />
        <Kpi label="Total Deductions" value={money(k.total_deductions)} />
        <Kpi label="Total Bonuses" value={money(k.total_bonuses)} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader><CardTitle className="text-base">Monthly Payroll Trend</CardTitle></CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={charts.monthly_trend || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="total" stroke="#0f766e" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Department-wise Payroll</CardTitle></CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={charts.department_distribution || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="department" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="total" fill="#3b82f6" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Paid vs Pending</CardTitle></CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={charts.paid_vs_pending || []} dataKey="value" nameKey="name" outerRadius={90} label>
                  {(charts.paid_vs_pending || []).map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Payroll Cost Trend</CardTitle></CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={charts.cost_trend || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="paid" fill="#0f766e" name="Paid" />
                <Bar dataKey="pending" fill="#f59e0b" name="Pending" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
